/**
 * Global style imports
 *
 * These must be imported first. Webpack will maintain source order, and we want to be able to override
 *   styles from 3rd party libraries with application styles.
 */

import '@deal/basscss/css/basscss.min.css'
import '@deal/components/dist/index.esm.css'
import 'slick-carousel/slick/slick.css'
import 'react-multi-email/style.css'
import 'react-toastify/dist/ReactToastify.css'
import 'react-image-lightbox/style.css'
import React, { Component } from 'react'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { withIdentity, WithIdentityProps } from '#src/containers/Identity'
import OpenGraph from '#src/components/OpenGraph'
import GridOverlay from '#src/components/GridOverlay'
import { DepartmentProvider } from '#src/context/department'
import { hot } from 'react-hot-loader/root'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { UserAcquisitionAttributes, setUserAcquisitionAttributes } from '#src/services/tracking'
import { Helmet } from 'react-helmet-async'
import NavigateOnEvent from '#src/containers/NavigateOnEvent'
import Router from '#src/routes'
import ScrollToTop from '#src/components/ScrollToTop'
import UnsupportedBrowserBanner from '#src/components/Banner'
import { UserAgentConsumer } from '#src/containers/UserAgent'
import config from '#src/config'
import styles from './styles.css'

// Favicons
import CuratedAppleTouchIcon from './favicons/curated/apple-touch-icon.png'
import CuratedFavicon16 from './favicons/curated/favicon-16x16.png'
import CuratedFavicon32 from './favicons/curated/favicon-32x32.png'
import isSupportedBrowser from '#src/services/unsupportedBrowsers'
import { MyselfFragment } from '#src/generated/graphql'
import { Analytics } from '#src/tracking'
import { Experiment, Variation } from '@deal/experiment-js'

class App extends Component<WithAnalyticsProps & WithIdentityProps & RouteComponentProps<{}>> {
  public unlisten: () => void
  private authBroadcastChannel: any = null

  constructor(props: WithAnalyticsProps & WithIdentityProps & RouteComponentProps<{}>) {
    super(props)

    this.unlisten = () => null
  }

  public componentDidMount() {
    // Pageview tracking
    this.unlisten = this.props.history.listen(() => {
      if (this.props.analytics) {
        this.props.analytics.page()
      }
    })

    // Initial pageview
    if (this.props.analytics) {
      this.props.analytics.page()
    }

    // If the user is logged in, save their acquisition attributes to local storage
    //   (in case they have switched devices from their previous session)
    if (this.props.myself) {
      this.setLocalAcquisitionAttributes(this.props.myself, this.props.analytics)
    }

    this.registerAuthChangeListener()
  }

  public componentDidUpdate(
    prevProps: WithAnalyticsProps & WithIdentityProps & RouteComponentProps<{}>
  ) {
    if (this.props.userId && this.props.userId !== prevProps.userId && this.props.myself) {
      this.setLocalAcquisitionAttributes(this.props.myself, this.props.analytics)
      this.broadcastOnAuthChange(this.props.myself.user.id)
    } else if (prevProps.myself && !this.props.myself) {
      this.broadcastOnAuthChange(null)
    }
  }

  public componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten()
    }
    if (this.authBroadcastChannel) {
      this.authBroadcastChannel.close()
    }
  }

  /**
   * Persist logged-in user's acquisition attributes (from the API) to local storage
   *  (in case they have switched devices from their previous session)
   */
  public setLocalAcquisitionAttributes(myself: MyselfFragment, analytics?: Analytics) {
    const source = myself && myself.user.source
    const attributes =
      source &&
      source.attributes.reduce<UserAcquisitionAttributes>((map, attr) => {
        map[attr.name as keyof UserAcquisitionAttributes] = attr.value
        return map
      }, {})

    if (attributes) {
      setUserAcquisitionAttributes(attributes, myself, analytics)
    }
  }

  private registerAuthChangeListener() {
    // To avoid any authorization errors, when the underlying identity changed,
    // we should redirect the user to the consumer home page.
    const app = this
    if ('BroadcastChannel' in self) {
      // @ts-ignore
      this.authBroadcastChannel = new BroadcastChannel('deal-consumer-auth')
      this.authBroadcastChannel.onmessage = (e: any) => {
        const message: any = e.data
        const currentUserId =
          app.props.myself && app.props.myself.user ? app.props.myself!.user!.id : null
        const newUserId = message.userId
        if (currentUserId !== newUserId) {
          const consumer = config.get('consumer')
          window.location.href = `${consumer.protocol}://${consumer.host}:${consumer.port}`
        }
      }
    }
    this.broadcastOnAuthChange(this.props.myself ? this.props.myself.user.id : null)
  }

  private broadcastOnAuthChange(newUserId: string | null) {
    // when underlying identity changed, broadcast the change.  All other tabs
    // with the same origin will receive the message and take appropriate action
    if (this.authBroadcastChannel) {
      this.authBroadcastChannel.postMessage({ userId: newUserId })
    }
  }

  public render() {
    return (
      <DepartmentProvider>
        <Helmet>
          <title>Curated</title>
          <meta
            name="description"
            content="Connect with experts on-demand to find the right products and services for you with ease. Get your personalized recommendation today."
          />
          <meta name="keywords" content="deal deals" />
          <meta name="msapplication-TileColor" content="#ffffff" />
          <meta name="theme-color" content="#ffffff" />
          <link rel="apple-touch-icon" sizes="180x180" href={CuratedAppleTouchIcon} />
          <link rel="icon" type="image/png" sizes="32x32" href={CuratedFavicon32} />
          <link rel="icon" type="image/png" sizes="16x16" href={CuratedFavicon16} />
        </Helmet>

        <Experiment id="a-a" default="a">
          <Variation id="a" />
          <Variation id="b" />
        </Experiment>

        <GridOverlay />

        <OpenGraph />

        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={false}
          closeOnClick={true}
          pauseOnHover={false}
          className={styles.toast}
        />

        <NavigateOnEvent />
        <UserAgentConsumer>
          {userAgent => {
            return (
              <>
                {!isSupportedBrowser(userAgent) && (
                  <UnsupportedBrowserBanner
                    text="Unsupported browser, some things might not work."
                    linkText="View compatable browsers"
                  />
                )}
              </>
            )
          }}
        </UserAgentConsumer>

        <ScrollToTop>
          <Router />
        </ScrollToTop>
      </DepartmentProvider>
    )
  }
}

export default hot(withIdentity(withAnalytics(withRouter(App))))
